import React, { useState, useCallback } from "react";
import {
  DocValue,
  GeneratedDocument,
} from "../../../services/encountersService";

interface DocumentDisplayProps {
  document: GeneratedDocument;
  // Optional key -> human label overrides; falls back to formatting the key
  sectionLabels?: Record<string, string>;
  onCopied?: (label: string) => void;
}

const isPlainObject = (v: DocValue): v is { [k: string]: DocValue } =>
  typeof v === "object" && v !== null && !Array.isArray(v);

// Full class strings (purge-safe) rotated across sections
const PALETTE = [
  { border: "border-blue-500", text: "text-blue-600" },
  { border: "border-green-500", text: "text-green-600" },
  { border: "border-yellow-500", text: "text-yellow-600" },
  { border: "border-purple-500", text: "text-purple-600" },
  { border: "border-pink-500", text: "text-pink-600" },
  { border: "border-teal-500", text: "text-teal-600" },
];

const formatLabel = (key: string): string =>
  key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const valueToText = (value: DocValue, indent = 0): string => {
  const pad = "  ".repeat(indent);
  if (Array.isArray(value)) {
    return value.map((v) => `${pad}• ${v}`).join("\n");
  }
  if (isPlainObject(value)) {
    return Object.entries(value)
      .map(([k, v]) => {
        const label = formatLabel(k);
        const inner = valueToText(v, indent + 1);
        return isPlainObject(v)
          ? `${pad}${label}:\n${inner}`
          : `${pad}${label}:\n${inner}`;
      })
      .join("\n");
  }
  return `${pad}${value}`;
};

const buildSectionMarkdown = (label: string, value: DocValue): string =>
  `${label.toUpperCase()}\n\n${valueToText(value)}`;

const DocumentDisplay: React.FC<DocumentDisplayProps> = ({
  document,
  sectionLabels,
  onCopied,
}) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = useCallback(
    async (key: string, label: string, value: DocValue) => {
      try {
        await navigator.clipboard.writeText(buildSectionMarkdown(label, value));
        setCopiedKey(key);
        onCopied?.(label);
        setTimeout(() => setCopiedKey(null), 2000);
      } catch {
        // Clipboard API unavailable
      }
    },
    [onCopied],
  );

  // Recursively render a section value (string / string[] / nested object)
  const renderValue = (value: DocValue, depth: number): React.ReactNode => {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <div className="text-gray-400 italic">No information provided</div>;
      }
      return (
        <ul className="list-disc list-inside ml-4 space-y-1">
          {value.map((item, i) => (
            <li key={i} className="text-gray-900">
              {item}
            </li>
          ))}
        </ul>
      );
    }
    if (isPlainObject(value)) {
      return (
        <div
          className={`space-y-3 ${depth > 0 ? "ml-4 pl-4 border-l border-gray-200" : ""}`}
        >
          {Object.entries(value).map(([k, v]) => (
            <div key={k}>
              <div className="font-semibold text-gray-700 mb-1">
                {formatLabel(k)}
              </div>
              {renderValue(v, depth + 1)}
            </div>
          ))}
        </div>
      );
    }
    const str = (value ?? "") as string;
    if (!str || str.trim() === "") {
      return <div className="text-gray-400 italic">No information provided</div>;
    }
    return <div className="text-gray-900 whitespace-pre-wrap">{str}</div>;
  };

  const entries = Object.entries(document || {});

  return (
    <div className="space-y-6">
      {entries.map(([key, value], index) => {
        const label = sectionLabels?.[key] || formatLabel(key);
        const color = PALETTE[index % PALETTE.length];

        return (
          <div
            key={key}
            className={`bg-white rounded-lg shadow-sm p-6 border-l-4 ${color.border}`}
          >
            <div className="flex items-center mb-4">
              <h3 className={`text-2xl font-bold ${color.text}`}>{label}</h3>
              <button
                onClick={() => handleCopy(key, label, value)}
                title={`Copy ${label} section`}
                className="ml-auto flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors
                  border-gray-200 text-gray-400 hover:text-gray-700 hover:border-gray-400
                  focus:outline-none focus:ring-1 focus:ring-blue-400"
              >
                {copiedKey === key ? (
                  <>
                    <svg
                      className="w-3.5 h-3.5 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-green-500 font-medium">Copied</span>
                  </>
                ) : (
                  <>
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
            <div className="space-y-2">{renderValue(value, 0)}</div>
          </div>
        );
      })}
    </div>
  );
};

export default DocumentDisplay;
